// Shopify register is a narrow variant of the shared signup command: validation, JSON register,
// challenge ownership and error mapping stay centralized in AuthSignupFlow. This flow owns only
// the reviewed opaque Shopify context and the context-aware Google entry URL (contract 06 §8).
import type { SignupPayload } from '../models/auth.types'

import { AuthSignupFlow, type AuthSignupDeps, type SignupOutcome } from './AuthSignupFlow'
import { buildGoogleAuthRedirectUrl, type ShopifyGoogleContext } from './googleAuthRedirect'

import { markSignup } from '@/features/auth/service/authAttribution'
import { useEnv } from '@/shared/config'

export type ShopifyRegistrationContext = ShopifyGoogleContext
export type ShopifyRegisterOutcome =
  SignupOutcome | { ok: false; contextError: 'auth.signup.shopifyContextInvalid' }

export function isShopifyRegistrationContext(
  value: Partial<ShopifyRegistrationContext>,
): value is ShopifyRegistrationContext {
  return Boolean(value.shop?.trim() && value.shopify_context?.trim())
}

export class AuthShopifyRegisterFlow {
  private readonly signupFlow: AuthSignupFlow
  private readonly redirect: (url: string) => void

  constructor(deps: AuthSignupDeps = {}) {
    this.signupFlow = new AuthSignupFlow(deps)
    this.redirect = deps.redirect ?? ((url) => window.location.assign(url))
  }

  async register(
    user: SignupPayload,
    terms: boolean,
    context: Partial<ShopifyRegistrationContext>,
  ): Promise<ShopifyRegisterOutcome> {
    if (!isShopifyRegistrationContext(context)) {
      return { ok: false, contextError: 'auth.signup.shopifyContextInvalid' }
    }
    const outcome = await this.signupFlow.submit(
      {
        ...user,
        shop: context.shop.trim(),
        shopify_context: context.shopify_context.trim(),
      },
      terms,
    )
    if (outcome.ok) markSignup('Shopify')
    return outcome
  }

  signupWithGoogle(context: Partial<ShopifyRegistrationContext>): boolean {
    if (!isShopifyRegistrationContext(context)) return false
    markSignup('Google')
    this.redirect(
      buildGoogleAuthRedirectUrl(useEnv().API_APP_URL, {
        shop: context.shop.trim(),
        shopify_context: context.shopify_context.trim(),
      }),
    )
    return true
  }
}
